import React from "react";
import { gql } from "apollo-boost";

import StatusBadge from "designV2/components/StatusBadge";
import SettingsList, { enumOptions, humanize } from "superAdminViews/setting/SettingsList";

const GET = gql`
  query getConstants($limit: Int, $skip: Int) {
    getConstants(pagination: { limit: $limit, skip: $skip }) {
      _id
      value
      attribute
      description
      typeOfAttribute
    }
  }
`;

const COUNT = gql`
  query getConstantsCount {
    getConstantsCount
  }
`;

const UPDATE = gql`
  mutation updateConstantByAdmin(
    $value: String!
    $typeOfAttribute: ConstantTypeOfAttribute!
    $description: String!
    $_id: ID
  ) {
    updateConstantByAdmin(
      filters: { _id: $_id }
      data: { value: $value, description: $description, typeOfAttribute: $typeOfAttribute }
    ) {
      _id
      value
      attribute
      description
      typeOfAttribute
    }
  }
`;

/* STRING was missing from the picker, so editing a text constant
   silently rewrote it as a number or a percentage. */
const TYPES = ["NUMBER", "PERCENTAGE", "STRING"];

const TONE = { PERCENTAGE: "info", NUMBER: "accent", STRING: "neutral" };

const formatValue = (row) => {
  if (row.typeOfAttribute === "PERCENTAGE") return `${row.value}%`;
  if (row.typeOfAttribute === "NUMBER") return `$${row.value}`;
  return row.value;
};

/**
 * Constants - the platform-wide numbers everything else is priced from.
 *
 * The set is fixed by the backend, so these can only be edited. Every
 * one of them lands on a customer's bill or a driver's payout, which is
 * why the description sits in the table rather than behind the dialog.
 */
const Constants = () => (
  <SettingsList
    standalone
    title="Constants"
    description="Platform-wide values — commissions, fees and thresholds — used across every trip and order."
    singular="constant"
    list={{ query: GET, key: "getConstants" }}
    count={{ query: COUNT, key: "getConstantsCount" }}
    update={{
      mutation: UPDATE,
      variables: (values, row) => ({
        _id: row._id,
        value: String(values.value).trim(),
        description: values.description.trim(),
        typeOfAttribute: values.typeOfAttribute,
      }),
    }}
    columns={[
      {
        key: "attribute",
        label: "Constant",
        width: "2fr",
        render: (row) => (
          <span className="settingsPrimaryCell">
            <strong>{humanize(row.attribute)}</strong>
            <span className="settingsClamp">{row.description}</span>
          </span>
        ),
      },
      { key: "value", label: "Value", render: formatValue },
      {
        key: "typeOfAttribute",
        label: "Kind",
        render: (row) => (
          <StatusBadge
            status={TONE[row.typeOfAttribute] || "neutral"}
            label={humanize(row.typeOfAttribute)}
          />
        ),
      },
    ]}
    fields={[
      {
        name: "value",
        label: "Value",
        type: "text",
        required: true,
        hint: "Enter the number on its own — no dollar sign or percent sign.",
      },
      {
        name: "typeOfAttribute",
        label: "Kind",
        type: "select",
        required: true,
        options: enumOptions(TYPES),
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        hint: "What this value controls. Only admins see it.",
      },
    ]}
    toValues={(row) => ({
      value: row.value,
      typeOfAttribute: row.typeOfAttribute,
      description: row.description || "",
    })}
  />
);

export default Constants;
